// CorporateFooter.jsx - Footer corporativo con información completa para Culqi
import React from 'react';
import { 
  Mail, Phone, MessageCircle, MapPin, FileText, Shield, 
  CreditCard, BookOpen, Facebook, Instagram, Clock,
  Building, Hash, ExternalLink, AlertCircle, Users
} from 'lucide-react';
import AnimatedLogo from './AnimatedLogo';

const CorporateFooter = ({ onOpenLibroReclamaciones }) => {
  const currentYear = new Date().getFullYear();
  
  // Información de la empresa
  const companyInfo = {
    name: 'MediExamPro',
    ruc: '10709315302',
    address: 'Lima, Perú',
    email: 'mediexampro@gmail.com',
    phone: '+51 964 088 671', // Cel oficial
    whatsapp: '+51 997 268 543' // WhatsApp de soporte
  };
  
  // Redes sociales
  const socialMedia = [
    { 
      name: 'Facebook', 
      url: 'https://facebook.com/MediexamPro',
      icon: <Facebook className="h-5 w-5" />,
      color: 'hover:bg-blue-600'
    },
    { 
      name: 'Instagram', 
      url: 'https://instagram.com/MediExamPro',
      icon: <Instagram className="h-5 w-5" />,
      color: 'hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-600'
    },
    { 
      name: 'TikTok', 
      url: 'https://www.tiktok.com/@MediExamPro',
      icon: (
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
        </svg>
      ),
      color: 'hover:bg-black'
    }
  ];
  
  // Enlaces rápidos
  const quickLinks = [
    { name: 'Planes y Precios', href: '/precios' },
    { name: 'Términos y Condiciones', href: '/terminos' },
    { name: 'Política de Privacidad', href: '/privacidad' },
    { name: 'Política de Devoluciones', href: '/devoluciones' },
    { name: 'Preguntas Frecuentes', href: '/faq' },
    { name: 'Contacto', href: '/contacto' }
  ];
  
  // Métodos de pago aceptados
  const paymentMethods = [
    'Visa', 'Mastercard', 'American Express', 'Diners Club', 'Yape', 'Plin'
  ];

  return (
    <footer className="bg-gradient-to-b from-gray-900 to-black text-white">
      {/* Sección principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          
          {/* Columna 1: Información de la empresa */}
          <div>
            <div className="mb-4">
              <AnimatedLogo 
                size="medium" 
                animated={true} 
                showText={true}
                interactive={false}
                variant="full"
              />
              <p className="text-xs text-gray-400 mt-2">Preparación Médica Premium</p>
            </div>
            
            <p className="text-sm text-gray-300 mb-4">
              Plataforma líder en preparación para exámenes de residencia médica en Perú.
              Más de 1,200 preguntas actualizadas y simulacros reales.
            </p>
            
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Building className="h-4 w-4 text-gray-400" />
                <span>RUC: {companyInfo.ruc}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-gray-400" />
                <span>{companyInfo.address}</span>
              </div>
            </div>
          </div>
          
          {/* Columna 2: Contacto */}
          <div>
            <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Phone className="h-5 w-5 text-blue-400" />
              Contacto Directo
            </h4>
            
            <div className="space-y-3">
              <a 
                href={`mailto:${companyInfo.email}`}
                className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors"
              >
                <Mail className="h-4 w-4" />
                <span className="text-sm">{companyInfo.email}</span>
              </a>
              
              <a 
                href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors"
              >
                <Phone className="h-4 w-4" />
                <span className="text-sm">{companyInfo.phone}</span>
              </a>
              
              <a 
                href={`https://wa.me/${companyInfo.whatsapp.replace(/[\s+]/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-gray-300 hover:text-green-400 transition-colors"
              >
                <MessageCircle className="h-4 w-4" />
                <span className="text-sm">WhatsApp: {companyInfo.whatsapp}</span>
              </a>
            </div>
            
            {/* Horario de atención */}
            <div className="mt-4 p-3 bg-gray-800 rounded-lg">
              <div className="flex items-center gap-2 text-sm mb-2">
                <Clock className="h-4 w-4 text-blue-400" />
                <span className="font-medium">Horario de Atención</span>
              </div>
              <p className="text-xs text-gray-400">
                Lun - Vie: 9:00 AM - 6:00 PM<br />
                Sábados: 9:00 AM - 1:00 PM
              </p>
            </div>
          </div>
          
          {/* Columna 3: Enlaces rápidos */}
          <div>
            <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-blue-400" />
              Enlaces Rápidos
            </h4>
            
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a 
                    href={link.href}
                    className="text-sm text-gray-300 hover:text-white transition-colors flex items-center gap-1"
                  >
                    <span className="text-blue-400">›</span>
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Columna 4: Redes sociales y métodos de pago */}
          <div>
            <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-400" />
              Síguenos
            </h4>
            
            {/* Redes sociales */}
            <div className="flex gap-2 mb-6">
              {socialMedia.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`bg-gray-800 p-2 rounded-lg hover:text-white transition-all ${social.color}`}
                  aria-label={social.name}
                >
                  {social.icon}
                </a>
              ))}
            </div>
            
            {/* Métodos de pago */}
            <div className="mb-4">
              <h5 className="text-sm font-medium mb-2 flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-blue-400" />
                Métodos de Pago Seguros
              </h5>
              <div className="grid grid-cols-3 gap-2">
                {paymentMethods.map((method) => (
                  <div 
                    key={method}
                    className="bg-gray-800 px-2 py-1 rounded text-xs text-center text-gray-300"
                  >
                    {method}
                  </div>
                ))}
              </div>
            </div>
            
            {/* SSL Badge */}
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <Shield className="h-4 w-4 text-green-400" />
              <span>Sitio Seguro SSL</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Libro de Reclamaciones */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <button
              onClick={onOpenLibroReclamaciones}
              className="flex items-center gap-3 bg-white text-gray-900 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-all transform hover:scale-105"
            >
              <FileText className="h-5 w-5" />
              <div className="text-left">
                <div className="text-sm font-bold">LIBRO DE RECLAMACIONES</div>
                <div className="text-xs text-gray-600">Conforme al Código del Consumidor</div>
              </div>
            </button>
            
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <AlertCircle className="h-4 w-4" />
              <span>
                Autorizado por INDECOPI | Resolución atendida en máximo 15 días hábiles
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Copyright y avisos legales */}
      <div className="bg-black/50 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-gray-500">
            <div className="flex items-center gap-4">
              <span>© {currentYear} MediExamPro. Todos los derechos reservados.</span>
              <span className="hidden md:inline">|</span>
              <span>RUC: {companyInfo.ruc}</span>
            </div>
            
            <div className="flex items-center gap-4">
              <a href="/terminos" className="hover:text-white transition-colors">
                Términos
              </a>
              <span>|</span>
              <a href="/privacidad" className="hover:text-white transition-colors">
                Privacidad
              </a>
              <span>|</span>
              <a href="/devoluciones" className="hover:text-white transition-colors">
                Devoluciones
              </a>
            </div>
          </div>
          
          {/* Disclaimer */}
          <div className="mt-4 pt-4 border-t border-gray-800 text-center text-xs text-gray-600">
            <p>
              MediExamPro es una plataforma educativa. El contenido no sustituye el consejo médico profesional.
              Procesamiento de pagos seguro mediante Culqi con encriptación SSL 256-bit.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default CorporateFooter;
